import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "@/App";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  BookOpen, Save, RefreshCw, ArrowLeft, ChevronDown, ChevronUp,
  Plus, Trash2, Eye, ExternalLink, CheckCircle, Target, Award,
  HelpCircle, Star, Zap, Info, AlertCircle
} from "lucide-react";
import { toast } from "sonner";

/* ────────────────────────────────────────────────────────────────────────────
   DESIGN TOKENS
──────────────────────────────────────────────────────────────────────────── */
const inputCls = "bg-white dark:bg-[#07090E] border border-gray-300 dark:border-white/[0.12] text-gray-900 dark:text-white text-sm mt-1 placeholder:text-gray-400 dark:placeholder:text-zinc-600 focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 rounded-md px-3 py-2 w-full outline-hidden transition-colors";
const cardCls  = "bg-white dark:bg-[#0E1623] border border-gray-200 dark:border-white/[0.08] shadow-xs rounded-xl overflow-hidden";


/* ────────────────────────────────────────────────────────────────────────────
   REUSABLE FIELD
──────────────────────────────────────────────────────────────────────────── */
const Field = ({ label, hint, required, children }) => (
  <div>
    <div className="flex items-center gap-1 mb-1">
      <label className="text-gray-500 dark:text-[#A1A1AA] text-xs font-medium">{label}</label>
      {required && <span className="text-red-400 text-xs">*</span>}
      {hint && (
        <div className="group relative">
          <Info className="w-3 h-3 text-zinc-600 cursor-help" />
          <div className="absolute bottom-full left-0 mb-1 w-52 bg-white dark:bg-[#1A2235] border border-gray-200 dark:border-white/10 text-xs text-zinc-300 rounded p-2 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50 whitespace-normal">
            {hint}
          </div>
        </div>
      )}
    </div>
    {children}
  </div>
);

/* ────────────────────────────────────────────────────────────────────────────
   COLLAPSIBLE SECTION
──────────────────────────────────────────────────────────────────────────── */
const Section = ({ title, icon: Icon, iconColor = "#10B981", badge, children, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className={cardCls}>
      <button
        onClick={() => setOpen(v => !v)}
        className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-white/[0.015] transition-colors"
      >
        <div className="flex items-center gap-2.5">
          {Icon && <Icon className="w-4 h-4 shrink-0" style={{ color: iconColor }} />}
          <span className="text-gray-900 dark:text-white font-outfit font-semibold text-sm">{title}</span>
          {badge !== undefined && (
            <span className="text-[10px] bg-white/[0.06] text-zinc-400 px-2 py-0.5 rounded-full font-mono">
              {badge} item{badge !== 1 ? "s" : ""}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {!open && <span className="text-[10px] text-zinc-600">Click to expand</span>}
          {open ? <ChevronUp className="w-4 h-4 text-zinc-500" /> : <ChevronDown className="w-4 h-4 text-zinc-500" />}
        </div>
      </button>
      {open && (
        <div className="px-5 pb-5 pt-1 space-y-4 border-t border-gray-100 dark:border-white/[0.04]">
          {children}
        </div>
      )}
    </div>
  );
};

/* ────────────────────────────────────────────────────────────────────────────
   EDITABLE BULLET LIST  (What You'll Learn / Who Is This For / Outcomes)
──────────────────────────────────────────────────────────────────────────── */
const BulletList = ({ items, onChange, placeholder, color = "#10B981" }) => {
  const list = Array.isArray(items) ? items : [];
  const update = (idx, val) => { const n = [...list]; n[idx] = val; onChange(n); };
  const remove = (idx) => onChange(list.filter((_, i) => i !== idx));
  const add    = () => onChange([...list, ""]);
  return (
    <div className="space-y-2">
      {list.map((item, idx) => (
        <div key={idx} className="flex items-center gap-2 group">
          <div className="w-1.5 h-1.5 rounded-full shrink-0 mt-0.5" style={{ background: color }} />
          <input
            value={item}
            onChange={e => update(idx, e.target.value)}
            placeholder={placeholder}
            className={inputCls + " flex-1"}
          />
          <button
            onClick={() => remove(idx)}
            className="opacity-0 group-hover:opacity-100 text-zinc-600 hover:text-red-400 transition-all"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      ))}
      <button
        onClick={add}
        className="flex items-center gap-1.5 text-xs font-medium mt-1 transition-colors"
        style={{ color }}
      >
        <Plus className="w-3.5 h-3.5" /> Add item
      </button>
    </div>
  );
};

/* ────────────────────────────────────────────────────────────────────────────
   HIGHLIGHT CHIPS  (label + value pairs)
──────────────────────────────────────────────────────────────────────────── */
const HighlightEditor = ({ items, onChange }) => {
  const list = Array.isArray(items) ? items : [];
  const upd = (idx, field, val) => { const n = [...list]; n[idx] = { ...n[idx], [field]: val }; onChange(n); };
  const rm  = (idx) => onChange(list.filter((_, i) => i !== idx));
  const add = () => onChange([...list, { label: "", value: "" }]);
  return (
    <div className="space-y-2">
      <div className="grid grid-cols-2 gap-2 text-[10px] text-zinc-600 uppercase tracking-widest font-bold px-1">
        <span>Label (e.g. "Duration")</span>
        <span>Value (e.g. "25 Sessions")</span>
      </div>
      {list.map((h, idx) => (
        <div key={idx} className="flex gap-2 items-center group">
          <input value={h.label || ""} onChange={e => upd(idx, "label", e.target.value)}
            placeholder="Duration" className={inputCls + " flex-1"} />
          <input value={h.value || ""} onChange={e => upd(idx, "value", e.target.value)}
            placeholder="25 Sessions" className={inputCls + " flex-1"} />
          <button onClick={() => rm(idx)}
            className="opacity-0 group-hover:opacity-100 text-zinc-600 hover:text-red-400 transition-all">
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      ))}
      <button onClick={add} className="flex items-center gap-1.5 text-xs text-emerald-600 dark:text-emerald-400 font-medium mt-1 hover:underline">
        <Plus className="w-3.5 h-3.5" /> Add chip
      </button>
    </div>
  );
};

/* ────────────────────────────────────────────────────────────────────────────
   FAQ EDITOR
──────────────────────────────────────────────────────────────────────────── */
const FaqEditor = ({ faqs, onChange }) => {
  const list = Array.isArray(faqs) ? faqs : [];
  const upd = (idx, field, val) => { const n = [...list]; n[idx] = { ...n[idx], [field]: val }; onChange(n); };
  const rm  = (idx) => onChange(list.filter((_, i) => i !== idx));
  const add = () => onChange([...list, { q: "", a: "" }]);
  const [expanded, setExpanded] = useState({});
  const toggle = (i) => setExpanded(p => ({ ...p, [i]: !p[i] }));

  return (
    <div className="space-y-2">
      {list.map((faq, idx) => (
        <div key={idx} className="border border-gray-200 dark:border-white/[0.06] rounded-lg overflow-hidden bg-white dark:bg-[#07090E]">
          <div className="flex items-center gap-2 px-3 py-2.5">
            <button onClick={() => toggle(idx)} className="flex-1 flex items-center gap-2 text-left min-w-0">
              <HelpCircle className="w-3.5 h-3.5 text-indigo-500 shrink-0" />
              <span className="text-sm text-gray-900 dark:text-white truncate font-medium">
                {faq.q || <span className="text-zinc-600 italic">Untitled question</span>}
              </span>
              {expanded[idx] ? <ChevronUp className="w-4 h-4 text-zinc-500 shrink-0 ml-auto" /> : <ChevronDown className="w-4 h-4 text-zinc-500 shrink-0 ml-auto" />}
            </button>
            <button onClick={() => rm(idx)} className="text-zinc-600 hover:text-red-400 transition-colors shrink-0">
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
          {expanded[idx] && (
            <div className="px-3 pb-3 space-y-2 border-t border-gray-100 dark:border-white/[0.04]">
              <Field label="Question">
                <input value={faq.q || ""} onChange={e => upd(idx, "q", e.target.value)}
                  placeholder="e.g. Do I need prior knowledge to join?" className={inputCls} />
              </Field>
              <Field label="Answer">
                <textarea value={faq.a || ""} onChange={e => upd(idx, "a", e.target.value)}
                  placeholder="Full answer here..." rows={4}
                  className={inputCls + " resize-none"} />
              </Field>
            </div>
          )}
        </div>
      ))}
      <button onClick={add} className="flex items-center gap-1.5 text-xs text-emerald-600 dark:text-emerald-400 font-medium mt-1 hover:underline">
        <Plus className="w-3.5 h-3.5" /> Add FAQ
      </button>
    </div>
  );
};

/* ────────────────────────────────────────────────────────────────────────────
   REVIEW EDITOR
──────────────────────────────────────────────────────────────────────────── */
const ReviewEditor = ({ reviews, onChange }) => {
  const list = Array.isArray(reviews) ? reviews : [];
  const upd = (idx, field, val) => { const n = [...list]; n[idx] = { ...n[idx], [field]: val }; onChange(n); };
  const rm  = (idx) => onChange(list.filter((_, i) => i !== idx));
  const add = () => onChange([...list, { name: "", role: "", review: "", rating: 5 }]);

  return (
    <div className="space-y-3">
      {list.map((rev, idx) => (
        <div key={idx} className="border border-gray-200 dark:border-white/[0.06] rounded-lg p-4 bg-white dark:bg-[#07090E] space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-800 flex items-center justify-center text-[10px] font-bold font-outfit">
                {(rev.name || "?").charAt(0).toUpperCase()}
              </div>
              <span className="text-sm font-semibold text-gray-900 dark:text-white">{rev.name || "New Review"}</span>
              <div className="flex gap-0.5">
                {Array.from({ length: rev.rating || 5 }).map((_, i) => (
                  <Star key={i} className="w-3 h-3 text-amber-400 fill-amber-400" />
                ))}
              </div>
            </div>
            <button onClick={() => rm(idx)} className="text-zinc-600 hover:text-red-400 transition-colors">
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Student Name">
              <input value={rev.name || ""} onChange={e => upd(idx, "name", e.target.value)}
                placeholder="Shubham Sain" className={inputCls} />
            </Field>
            <Field label="Role / Profession">
              <input value={rev.role || ""} onChange={e => upd(idx, "role", e.target.value)}
                placeholder="Student / Trader" className={inputCls} />
            </Field>
          </div>
          <Field label="Review Text">
            <textarea value={rev.review || ""} onChange={e => upd(idx, "review", e.target.value)}
              placeholder="Student review here..." rows={3}
              className={inputCls + " resize-none"} />
          </Field>
          <Field label="Star Rating (1-5)">
            <div className="flex items-center gap-2 mt-1">
              {[1, 2, 3, 4, 5].map(n => (
                <button key={n} onClick={() => upd(idx, "rating", n)}
                  className={`w-7 h-7 rounded text-xs font-bold transition-all ${(rev.rating || 5) >= n ? "bg-[#FFCC00]/20 text-[#FFCC00] border border-[#FFCC00]/40" : "bg-gray-50 dark:bg-white/[0.03] text-zinc-600 border border-gray-200 dark:border-white/[0.06]"}`}>
                  {n}★
                </button>
              ))}
            </div>
          </Field>
        </div>
      ))}
      <button onClick={add} className="flex items-center gap-1.5 text-xs text-[#FFCC00] font-medium mt-1">
        <Plus className="w-3.5 h-3.5" /> Add Review
      </button>
    </div>
  );
};

/* ────────────────────────────────────────────────────────────────────────────
   COURSE LIST CARD
──────────────────────────────────────────────────────────────────────────── */
const CourseCard = ({ course, onSelect }) => (
  <div
    onClick={() => onSelect(course)}
    className="group flex gap-4 p-4 bg-white dark:bg-[#0E1623] border border-gray-200 dark:border-white/[0.06] rounded-xl hover:border-emerald-500/40 hover:bg-slate-50 dark:hover:bg-[#0E1623]/80 transition-all cursor-pointer"
  >
    {course.thumbnail ? (
      <div className="w-24 h-16 rounded-lg overflow-hidden shrink-0 border border-gray-200 dark:border-white/[0.06]">
        <img src={course.thumbnail} alt={course.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
      </div>
    ) : (
      <div className="w-24 h-16 rounded-lg bg-white/[0.04] flex items-center justify-center shrink-0">
        <BookOpen className="w-6 h-6 text-zinc-600" />
      </div>
    )}
    <div className="flex-1 min-w-0">
      <div className="flex flex-wrap items-center gap-2 mb-1">
        <h3 className="text-gray-900 dark:text-white font-outfit font-semibold text-sm group-hover:text-emerald-600 dark:group-hover:text-emerald-400 transition-colors">{course.title}</h3>
        <Badge className={`text-[10px] border px-2 py-0.5 rounded-full ${course.is_free ? "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-800" : "bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950/40 dark:text-amber-300 dark:border-amber-800"}`}>
          {course.is_free ? "FREE" : course.price_display}
        </Badge>
        <Badge className={`text-[10px] border-0 px-2 py-0 ${course.is_active ? "bg-blue-500/10 text-blue-400" : "bg-red-500/10 text-red-400"}`}>
          {course.is_active ? "● Live" : "● Hidden"}
        </Badge>
      </div>
      <p className="text-xs text-zinc-500 line-clamp-1 mb-1">{course.description}</p>
      <div className="flex items-center gap-3 text-[10px] text-zinc-600 font-mono">
        <span>/courses/{course.slug || course.course_id}</span>
        {course.sessions && <span>{course.sessions} sessions</span>}
      </div>
    </div>
    <div className="flex items-center shrink-0">
      <span className="text-xs text-emerald-600 dark:text-emerald-400 font-medium opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1">
        Edit Content <ChevronDown className="w-3.5 h-3.5 rotate-[-90deg]" />
      </span>
    </div>
  </div>
);

/* ════════════════════════════════════════════════════════════════════════════
   MAIN COMPONENT
════════════════════════════════════════════════════════════════════════════ */
const CourseManager = () => {
  const [courses,    setCourses]    = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [selected,   setSelected]   = useState(null);   // full course object
  const [metaLoading, setMetaLoading] = useState(false);
  const [basicSaving, setBasicSaving] = useState(false);
  const [metaSaving,  setMetaSaving]  = useState(false);
  const [dirty,      setDirty]      = useState(false);

  // Editor state — basic info
  const [basic, setBasic] = useState({});
  // Editor state — meta/content
  const [meta, setMeta] = useState({});

  /* ── fetch course list ── */
  const fetchCourses = useCallback(async () => {
    setLoading(true);
    try {
      // Try admin endpoint first (all courses incl. hidden)
      let list = [];
      try {
        const res = await axios.get(`${API}/admin/courses`, { withCredentials: true });
        list = Array.isArray(res.data) ? res.data : (res.data.courses || []);
      } catch (adminErr) {
        // Fall back to public endpoint (active courses only)
        const res = await axios.get(`${API}/courses`);
        list = Array.isArray(res.data) ? res.data : (res.data.courses || []);
      }
      setCourses(list);
      if (list.length === 0) {
        toast("No courses found. Create courses in the Admin Dashboard first.");
      }
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || "Unknown error";
      toast.error(`Failed to load courses: ${msg}`);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchCourses(); }, [fetchCourses]);

  /* ── open course for editing ── */
  const openCourse = async (course) => {
    setMetaLoading(true);
    setSelected(course);
    setBasic({
      title:         course.title         || "",
      description:   course.description   || "",
      thumbnail:     course.thumbnail     || "",
      price:         course.price         ?? 0,
      price_display: course.price_display || "",
      sessions:      course.sessions      || "",
      slug:          course.slug          || "",
      is_free:       course.is_free       || false,
      is_active:     course.is_active     !== false,
      category:      course.category      || "",
    });

    try {
      // Try new admin full endpoint first, fall back to public detail endpoint
      let m = {};
      try {
        const res = await axios.get(`${API}/admin/courses/${course.course_id}/full`, { withCredentials: true });
        m = res.data.meta || {};
      } catch {
        // Fall back to public detail endpoint (already has meta)
        try {
          const res = await axios.get(`${API}/courses/detail/${course.course_id}`);
          m = res.data.meta || {};
        } catch {
          m = {};
        }
      }
      setMeta({
        tagline:                m.tagline                || "",
        highlights:             m.highlights             || [],
        what_you_learn:         m.what_you_learn         || [],
        who_is_for:             m.who_is_for             || [],
        outcomes:               m.outcomes               || [],
        faqs:                   m.faqs                   || [],
        reviews:                m.reviews                || [],
        google_reviews_heading: m.google_reviews_heading || "What Traders Say on Google",
        google_rating:          m.google_rating          || "4.9",
        google_review_count:    m.google_review_count    || "90+",
        cta_heading:            m.cta_heading            || "",
        cta_description:        m.cta_description        || "",
      });
    } catch {
      toast.error("Failed to load course content");
    } finally {
      setMetaLoading(false);
      setDirty(false);
    }
  };

  /* ── update helpers ── */
  const updBasic = (k, v) => { setBasic(p => ({ ...p, [k]: v })); setDirty(true); };
  const updMeta  = (k, v) => { setMeta(p => ({ ...p, [k]: v }));  setDirty(true); };

  /* ── save basic info ── */
  const saveBasic = async () => {
    setBasicSaving(true);
    try {
      await axios.put(`${API}/admin/courses/${selected.course_id}`, basic, { withCredentials: true });
      toast.success("✅ Basic course info saved!");
      fetchCourses();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save basic info");
    } finally { setBasicSaving(false); }
  };

  /* ── save meta (content) ── */
  const saveMeta = async () => {
    setMetaSaving(true);
    try {
      await axios.put(`${API}/admin/courses/${selected.course_id}/meta`, meta, { withCredentials: true });
      toast.success("✅ Course content saved! Changes are live on the website.");
      setDirty(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save content");
    } finally { setMetaSaving(false); }
  };

  /* ════════════════════
     COURSE LIST VIEW
  ════════════════════ */
  if (!selected) {
    return (
      <div className="space-y-5">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-gray-900 dark:text-white font-outfit font-bold flex items-center gap-2 text-base">
              <BookOpen className="w-5 h-5 text-emerald-600 dark:text-emerald-400" /> Course Page Content Manager
            </h2>
            <p className="text-xs text-zinc-500 mt-0.5">
              Select a course to edit its page content. You can change FAQs, reviews, descriptions, highlights, and more.
              <br /><span className="text-zinc-600">To create or delete courses, use the Admin Dashboard.</span>
            </p>
          </div>
          <Button variant="ghost" size="sm" onClick={fetchCourses} disabled={loading}
            className="text-zinc-400 hover:text-gray-900 dark:text-white rounded-xl">
            <RefreshCw className={`w-4 h-4 mr-1.5 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
        </div>

        {loading ? (
          <div className="py-16 flex flex-col items-center gap-3">
            <RefreshCw className="w-8 h-8 text-emerald-600 dark:text-emerald-400 animate-spin" />
            <p className="text-xs text-zinc-500">Loading courses...</p>
          </div>
        ) : courses.length === 0 ? (
          <div className="py-16 text-center border border-dashed border-gray-200 dark:border-white/[0.08] rounded-xl">
            <BookOpen className="w-12 h-12 mx-auto mb-3 text-zinc-700" />
            <p className="text-zinc-400 font-outfit font-medium mb-1">No courses found</p>
            <p className="text-xs text-zinc-600">Create courses in the Admin Dashboard, then come back here to edit their content.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {courses.map(c => <CourseCard key={c.course_id} course={c} onSelect={openCourse} />)}
          </div>
        )}
      </div>
    );
  }

  /* ════════════════════
     COURSE EDITOR VIEW
  ════════════════════ */
  return (
    <div className="space-y-4 pb-24">

      {/* ── sticky header ── */}
      <div className="sticky top-0 z-30 bg-white dark:bg-[#080C14]/95 backdrop-blur-xl border-b border-gray-200 dark:border-white/[0.06] -mx-1 px-1 py-3 flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <button onClick={() => setSelected(null)} className="text-zinc-400 hover:text-gray-900 dark:text-white p-1 rounded hover:bg-white/[0.04] transition-all">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="min-w-0">
            <p className="text-[11px] text-zinc-500 uppercase tracking-widest font-bold mb-0.5">Editing Course</p>
            <h2 className="text-gray-900 dark:text-white font-outfit font-bold truncate text-sm">{basic.title || selected.title}</h2>
          </div>
          {dirty && (
            <span className="text-[10px] bg-amber-500/10 text-amber-400 border border-amber-500/20 px-2 py-0.5 rounded-full font-medium shrink-0">
              Unsaved changes
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <a href={`/courses/${selected.slug || selected.course_id}`} target="_blank" rel="noopener noreferrer">
            <Button size="sm" variant="ghost" className="text-zinc-400 hover:text-gray-900 dark:text-white h-8 px-3">
              <ExternalLink className="w-3.5 h-3.5 mr-1.5" /> View Live Page
            </Button>
          </a>
          <Button onClick={saveMeta} disabled={metaSaving} size="sm"
            className="bg-emerald-600 hover:bg-emerald-700 text-white font-medium shadow-xs rounded-xl transition-all h-8 px-4">
            {metaSaving ? <><RefreshCw className="w-3.5 h-3.5 mr-1.5 animate-spin" />Saving...</> : <><Save className="w-3.5 h-3.5 mr-1.5" />Save Changes</>}
          </Button>
        </div>
      </div>

      {metaLoading && (
        <div className="py-12 flex flex-col items-center gap-2">
          <RefreshCw className="w-7 h-7 text-emerald-600 dark:text-emerald-400 animate-spin" />
          <p className="text-xs text-zinc-500">Loading course content...</p>
        </div>
      )}

      {!metaLoading && (
        <>
          {/* ── BASIC INFO ── */}
          <Section title="Basic Course Info" icon={BookOpen} iconColor="#10B981" defaultOpen={true}>
            <div className="bg-emerald-50/50 dark:bg-emerald-950/20 border border-emerald-200 dark:border-emerald-800/60 rounded-xl px-4 py-3 flex items-start gap-2 text-xs text-emerald-800 dark:text-emerald-300">
              <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-0.5 text-emerald-600 dark:text-emerald-400" />
              After editing basic info, click <strong className="text-emerald-700 dark:text-emerald-300">"Save Basic Info"</strong> below. The green "Save Changes" button at the top saves only course content (FAQs, reviews, etc.).
            </div>

            <Field label="Course Title" required>
              <input value={basic.title} onChange={e => updBasic("title", e.target.value)} className={inputCls} />
            </Field>

            <Field label="Short Tagline" hint="Shown in bold under the title on the course page hero">
              <input value={meta.tagline} onChange={e => updMeta("tagline", e.target.value)}
                placeholder="Master the institutional trading methodology used by smart money" className={inputCls} />
            </Field>

            <Field label="Full Description" hint="Main paragraph shown on the hero section">
              <textarea value={basic.description} onChange={e => updBasic("description", e.target.value)}
                rows={3} className={inputCls + " resize-none"} />
            </Field>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Thumbnail Image URL">
                <input value={basic.thumbnail} onChange={e => updBasic("thumbnail", e.target.value)}
                  placeholder="https://..." className={inputCls} />
              </Field>
              <Field label="URL Slug" hint="Course URL: /courses/[slug]">
                <input value={basic.slug} onChange={e => updBasic("slug", e.target.value)}
                  placeholder="super-trader-edge" className={inputCls} />
              </Field>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <Field label="Price (₹)" hint="Set to 0 for free">
                <input type="number" value={basic.price}
                  onChange={e => updBasic("price", parseFloat(e.target.value) || 0)} className={inputCls} />
              </Field>
              <Field label="No. of Sessions">
                <input type="number" value={basic.sessions}
                  onChange={e => updBasic("sessions", parseInt(e.target.value) || 0)} className={inputCls} />
              </Field>
              <Field label="Category">
                <input value={basic.category || ""} onChange={e => updBasic("category", e.target.value)}
                  placeholder="Trading" className={inputCls} />
              </Field>
              <Field label="Visibility">
                <select value={basic.is_active ? "active" : "hidden"}
                  onChange={e => updBasic("is_active", e.target.value === "active")}
                  className={inputCls}>
                  <option value="active">✅ Active (visible)</option>
                  <option value="hidden">🔴 Hidden</option>
                </select>
              </Field>
            </div>

            <div className="flex justify-end pt-2">
              <Button onClick={saveBasic} disabled={basicSaving} size="sm"
                className="bg-emerald-50 hover:bg-emerald-100 text-emerald-700 dark:bg-emerald-950/50 dark:hover:bg-emerald-900/50 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800 font-semibold transition-all px-5 rounded-xl shadow-xs">
                {basicSaving ? <><RefreshCw className="w-3.5 h-3.5 mr-2 animate-spin" />Saving...</> : <><Save className="w-3.5 h-3.5 mr-2" />Save Basic Info</>}
              </Button>
            </div>
          </Section>

          {/* ── HIGHLIGHT CHIPS ── */}
          <Section title="Info Chips / Highlights" icon={Zap} iconColor="#6366F1"
            badge={meta.highlights?.length ?? 0}
            hint="Small boxes shown below title: '25 Sessions', 'Live Online', etc.">
            <p className="text-xs text-zinc-500 -mt-2">
              These appear as small info boxes in the hero — e.g. "Duration: 25 Sessions", "Mode: Live Online", "Recordings: Lifetime Access"
            </p>
            <HighlightEditor items={meta.highlights} onChange={v => updMeta("highlights", v)} />
          </Section>

          {/* ── WHAT YOU'LL LEARN ── */}
          <Section title="What You'll Learn" icon={CheckCircle} iconColor="#10B981"
            badge={meta.what_you_learn?.length ?? 0}>
            <p className="text-xs text-zinc-500 -mt-2">
              Each item becomes a green checkmark bullet in the curriculum section.
            </p>
            <BulletList
              items={meta.what_you_learn}
              onChange={v => updMeta("what_you_learn", v)}
              placeholder="e.g. Advanced Demand & Supply zone identification"
              color="#10B981"
            />
          </Section>

          {/* ── WHO IS THIS FOR ── */}
          <Section title="Who Is This Course For" icon={Target} iconColor="#F59E0B"
            badge={meta.who_is_for?.length ?? 0}>
            <p className="text-xs text-zinc-500 -mt-2">
              Each item becomes a yellow target bullet in the "For You" section.
            </p>
            <BulletList
              items={meta.who_is_for}
              onChange={v => updMeta("who_is_for", v)}
              placeholder="e.g. Traders who know basics but struggle with consistency"
              color="#F59E0B"
            />
          </Section>

          {/* ── OUTCOMES ── */}
          <Section title="Outcomes / Results" icon={Award} iconColor="#10B981"
            badge={meta.outcomes?.length ?? 0}>
            <p className="text-xs text-zinc-500 -mt-2">
              What students achieve after finishing this course.
            </p>
            <BulletList
              items={meta.outcomes}
              onChange={v => updMeta("outcomes", v)}
              placeholder="e.g. Execute trades with institutional-grade precision"
              color="#10B981"
            />
          </Section>

          {/* ── FAQs ── */}
          <Section title="FAQs — Frequently Asked Questions" icon={HelpCircle} iconColor="#06B6D4"
            badge={meta.faqs?.length ?? 0}>
            <p className="text-xs text-zinc-500 -mt-2">
              These appear in the FAQ accordion at the bottom of the course page. You can add, edit, reorder, and remove them.
            </p>
            <FaqEditor faqs={meta.faqs} onChange={v => updMeta("faqs", v)} />
          </Section>

          {/* ── REVIEWS ── */}
          <Section title="Student Reviews / Testimonials" icon={Star} iconColor="#FFCC00"
            badge={meta.reviews?.length ?? 0}>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 -mt-1 mb-2">
              <Field label="Reviews Section Heading">
                <input value={meta.google_reviews_heading || ""} onChange={e => updMeta("google_reviews_heading", e.target.value)}
                  placeholder="What Traders Say on Google" className={inputCls} />
              </Field>
              <Field label="Overall Rating (e.g. 4.9)">
                <input value={meta.google_rating || ""} onChange={e => updMeta("google_rating", e.target.value)}
                  placeholder="4.9" className={inputCls} />
              </Field>
              <Field label="Review Count (e.g. 90+)">
                <input value={meta.google_review_count || ""} onChange={e => updMeta("google_review_count", e.target.value)}
                  placeholder="90+" className={inputCls} />
              </Field>
            </div>
            <ReviewEditor reviews={meta.reviews} onChange={v => updMeta("reviews", v)} />
          </Section>

          {/* ── CTA SECTION ── */}
          <Section title="Enroll CTA Section (Bottom of page)" icon={Zap} iconColor="#10B981">
            <p className="text-xs text-zinc-500 -mt-2">
              The "Ready to Level Up?" box shown at the bottom with an Enroll Now button.
            </p>
            <Field label="CTA Heading" hint="e.g. 'Ready to Level Up?'">
              <input value={meta.cta_heading || ""} onChange={e => updMeta("cta_heading", e.target.value)}
                placeholder="Ready to Level Up?" className={inputCls} />
            </Field>
            <Field label="CTA Description">
              <textarea value={meta.cta_description || ""} onChange={e => updMeta("cta_description", e.target.value)}
                rows={2} className={inputCls + " resize-none"} />
            </Field>
          </Section>

          {/* ── final save ── */}
          <div className="flex items-center justify-between pt-2 border-t border-gray-200 dark:border-white/[0.06]">
            <p className="text-xs text-zinc-600">All content changes saved to the live website instantly.</p>
            <Button onClick={saveMeta} disabled={metaSaving} size="lg"
              className="bg-emerald-600 hover:bg-emerald-700 text-white font-medium shadow-xs rounded-xl transition-all px-8 h-10">
              {metaSaving
                ? <><RefreshCw className="w-4 h-4 mr-2 animate-spin" />Saving...</>
                : <><Save className="w-4 h-4 mr-2" />Save All Course Content</>
              }
            </Button>
          </div>
        </>
      )}
    </div>
  );
};

export default CourseManager;
